// Internationalization configuration and route mapping for Danshaker
export const LANGUAGES = [
  { code: 'da', label: 'Dansk', shortLabel: 'DA', flag: '🇩🇰', pathPrefix: '' },
  { code: 'en', label: 'English', shortLabel: 'EN', flag: '🇬🇧', pathPrefix: '/en' },
  { code: 'de', label: 'Deutsch', shortLabel: 'DE', flag: '🇩🇪', pathPrefix: '/de' },
];

export const CANONICAL_DOMAIN = 'https://danshaker.dk';

// Route equivalence mapping across languages
export const ROUTE_MAP = [
  {
    key: 'home',
    da: '/',
    en: '/en',
    de: '/de',
  },
  {
    key: 'product',
    da: '/produkt',
    en: '/en/product',
    de: '/de/produkt',
  },
  {
    key: 'accessories',
    da: '/tilbehoer',
    en: '/en/accessories',
    de: '/de/zubehoer',
  },
  {
    key: 'calculator',
    da: '/beregner',
    en: '/en/calculator',
    de: '/de/sparrechner',
  },
  {
    key: 'rent',
    da: '/leje',
    en: '/en/rent',
    de: '/de/miete',
  },
  {
    key: 'commercial-leasing',
    da: '/erhvervsleasing',
    en: '/en/commercial-leasing',
    de: '/de/gewerbeleasing',
  },
  {
    key: 'private-financing',
    da: '/privatfinansiering',
    en: '/en/private-financing',
    de: '/de/privatfinanzierung',
  },
  {
    key: 'service',
    da: '/service',
    en: '/en/service',
    de: '/de/service',
  },
  {
    key: 'demo',
    da: '/gratis-demo',
    en: '/en/free-demo',
    de: '/de/kostenlose-demo',
  },
  {
    key: 'about',
    da: '/om-danshaker',
    en: '/en/about-danshaker',
    de: '/de/ueber-danshaker',
  },
  {
    key: 'events',
    da: '/moed-os-her',
    en: '/en/meet-us',
    de: '/de/uns-treffen',
  },
  {
    key: 'news',
    da: '/nyheder',
    en: '/en/news',
    de: '/de/neuigkeiten',
  },
  {
    key: 'news-personal',
    da: '/nyheder/goer-din-danshaker-personlig',
    en: '/en/news/customize-your-danshaker',
    de: '/de/neuigkeiten/personalisieren-sie-ihren-danshaker',
  },
  {
    key: 'news-financing',
    da: '/nyheder/privatfinansiering',
    en: '/en/news/private-financing',
    de: '/de/neuigkeiten/privatfinanzierung',
  },
  {
    key: 'reviews',
    da: '/kundeudtalelser',
    en: '/en/reviews',
    de: '/de/kundenstimmen',
  },
  {
    key: 'faq',
    da: '/faq',
    en: '/en/faq',
    de: '/de/faq',
  },
  {
    key: 'contact',
    da: '/kontakt',
    en: '/en/contact',
    de: '/de/kontakt',
  },
];

// Normalize trailing slashes for route matching (except root '/')
export function normalizePath(path = '') {
  if (!path || path === '/') return '/';
  return path.replace(/\/+$/, '') || '/';
}

// Detect language from URL pathname
export function getLanguageFromPath(pathname = '/') {
  const cleanPath = normalizePath(pathname);
  if (cleanPath === '/en' || cleanPath.startsWith('/en/')) {
    return 'en';
  }
  if (cleanPath === '/de' || cleanPath.startsWith('/de/')) {
    return 'de';
  }
  return 'da';
}

// Get equivalent path in target language
export function getEquivalentPath(currentPathname = '/', targetLang = 'da') {
  const cleanPath = normalizePath(currentPathname);

  // Exact match in route map
  for (const item of ROUTE_MAP) {
    if (
      normalizePath(item.da) === cleanPath ||
      normalizePath(item.en) === cleanPath ||
      normalizePath(item.de) === cleanPath
    ) {
      return item[targetLang] || item.da;
    }
  }

  // Handle dynamic news routes if unknown slug
  if (cleanPath.startsWith('/nyheder/')) {
    const slug = cleanPath.replace('/nyheder/', '');
    if (targetLang === 'en') return `/en/news/${slug}`;
    if (targetLang === 'de') return `/de/neuigkeiten/${slug}`;
    return `/nyheder/${slug}`;
  }
  if (cleanPath.startsWith('/en/news/')) {
    const slug = cleanPath.replace('/en/news/', '');
    if (targetLang === 'da') return `/nyheder/${slug}`;
    if (targetLang === 'de') return `/de/neuigkeiten/${slug}`;
    return `/en/news/${slug}`;
  }
  if (cleanPath.startsWith('/de/neuigkeiten/')) {
    const slug = cleanPath.replace('/de/neuigkeiten/', '');
    if (targetLang === 'da') return `/nyheder/${slug}`;
    if (targetLang === 'en') return `/en/news/${slug}`;
    return `/de/neuigkeiten/${slug}`;
  }

  // Safe fallback to target language home
  if (targetLang === 'en') return '/en';
  if (targetLang === 'de') return '/de';
  return '/';
}

// Get all hreflang URLs for a given path
export function getHreflangLinks(currentPathname = '/') {
  const daPath = getEquivalentPath(currentPathname, 'da');
  const enPath = getEquivalentPath(currentPathname, 'en');
  const dePath = getEquivalentPath(currentPathname, 'de');

  return {
    da: `${CANONICAL_DOMAIN}${daPath}`,
    en: `${CANONICAL_DOMAIN}${enPath}`,
    de: `${CANONICAL_DOMAIN}${dePath}`,
    'x-default': `${CANONICAL_DOMAIN}${daPath}`,
  };
}
